import * as Sentry from '@sentry/react';
import { Dispatch, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router';
import useSWR from 'swr';

import { RoleType } from '@/types/User';

import API from './api';

interface AuthHookProps {
    middleware?: 'guest' | 'auth';
    redirectTo?: string;
    tmp: string;
}

interface LoginProps {
    values: {
        email: string;
        password: string;
    };
    setError: Dispatch<any>;
}

interface VerifyProps {
    values: {
        email: string;
        token: string;
    };
    setError: Dispatch<any>;
}

interface ForgotPasswordProps {
    email: string;
}

interface RestPasswordProps {
    email: string;
    password: string;
    password_confirmation: string;
}

export interface UserProps {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    roles: RoleType[];
    sia: boolean;
    subscriptionEndsAt: string | null;
    fpr?: string;
    currentAccountId: string;
    // features: string[];
}

// Only a same-origin path, so that ?redirect= cannot send the user to another site.
export const safeRedirect = (value: string | null) =>
    value?.startsWith('/') && !value.startsWith('//') ? value : '/';

export const useAuth = ({ middleware, redirectTo, tmp }: AuthHookProps) => {
    const navigate = useNavigate();
    const location = useLocation();

    // console.trace('useAuth')

    const {
        data: user,
        mutate,
        isLoading,
        error,
    } = useSWR<UserProps>('/api/v2/me', (url: string) =>
        API.get(url)
            .then((res) => {
                return res.data;
            })
            .catch((error) => {
                if (error.code === 'ECONNABORTED') return;

                // throw error
                return null;
            })
    );

    const csrf = () => API.get('/api/v2/auth/csrf-token');

    const login = async ({ values, setError }: LoginProps) => {
        setError(false);
        await csrf();

        return API.post('/api/v2/auth/login', values)
            .then((res) => {
                const sia = res?.data?.user?.sia;
                // @ts-expect-error window is not defined
                window.dataLayer?.push({
                    event: 'login',
                    sia,
                });

                mutate().then(() => navigate(safeRedirect(new URLSearchParams(location.search).get('redirect'))));

                return res;
            })
            .catch((error) => {
                setError(error.response.data?.message);

                throw error;
            });
    };

    const loginNonce = async ({ values, setError }: VerifyProps) => {
        setError(false);
        await csrf();

        return API.post('/api/v2/auth/nonce', values)
            .then((res) => {
                const sia = res?.data?.user?.sia;
                // @ts-expect-error window is not defined
                window.dataLayer?.push({
                    event: 'login',
                    sia,
                });

                return res;
            })
            .catch((error) => {
                setError(error.response.data?.message);

                throw error;
            });
    };

    const resendVerification = async ({ email }: { email: string }) => {
        await csrf();

        return API.post('/api/v2/auth/verify', { email });
    };

    const forgotPassword = async ({ email }: ForgotPasswordProps) => {
        await csrf();

        return API.post('/api/v2/forgot-passwords', { email })
            .then((res) => {
                toast.success(res?.data?.status);
            })
            .catch((error) => {
                if (error.response.status !== 422) throw error;
            });
    };

    const resetPassword = async ({
        email,
        password,
        password_confirmation,
    }: RestPasswordProps) => {
        await csrf();

        return API.post('/reset-password', {
            email,
            password,
            password_confirmation,
        })
            .then((res) => {
                toast.success(res?.data?.status);
                // navigate('/');
            })
            .catch((error) => {
                if (error.response.status !== 422) throw error;
            });
    };

    const logout = async () => {
        return API.post('/api/v2/auth/logout')
            .then(() => {
                mutate(undefined, false);
                navigate('/auth/login');
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    console.log(401);
                    return mutate();
                }

                throw error;
            });
    };

    useEffect(() => {
        // console.log(middleware, tmp);
        if (!middleware || isLoading) return;

        if (middleware === 'guest' && user) {
            navigate(redirectTo ?? safeRedirect(new URLSearchParams(location.search).get('redirect')));
            return;
        }

        if (error?.response?.status === 503) {
            navigate('/maintenance?ref=' + location.pathname);
            return;
        }

        if (middleware === 'auth' && !user) {
            const back = location.pathname + location.search;
            navigate(back === '/' ? '/auth/login' : '/auth/login?redirect=' + encodeURIComponent(back));
            return;
        }
    }, [middleware, user, error, redirectTo, tmp, isLoading, navigate]);

    useEffect(() => {
        if (user) {
            Sentry.setUser({
                id: user.id,
                currentAccountId: user.currentAccountId,
            });
        }
    }, [user]);

    return {
        user,
        error,
        csrf,
        // userHasFullLicense: user?.institution.license === 'FULL',
        isLoading,
        login,
        loginNonce,
        // loginUsingGoogle,
        resendVerification,
        forgotPassword,
        resetPassword,
        logout,
    };
};
